import React, { useState } from 'react';
import { ThemeContext } from '../../lib/ThemeContext.js';
import { FORM_ROOT_CLASS, formRootAttributes } from '~/lib/FormCssApi';
import { isEmbedded } from '../../public/lib/PageHelpers.js';
import { formThemeTokens } from '~/lib/FormTheme';
import { FormCustomCssProvider } from '~/lib/FormCustomCssContext';

function getTheme(value, options) {
    if (value && options[value]) {
        return options[value];
    } else {
        return options[Object.keys(options)[0]];
    }
}

const ThemedForm = ({
    styles,
    onlyColor,
    objectType,
    objectId,
    state,
    step,
    customCss,
    scopeRoot = true,
    children,
}) => {
    const [scope, setScope] = useState(
        `theme-${parseInt(Math.random() * 100000000)}`,
    );

    const inputTheme = getTheme(styles?.input, {
        'rounded-rectangle': 'theme-input-rounded-rectangle',
        square: 'theme-input-square',
        pill: 'theme-input-pill',
    });

    const buttonTheme = getTheme(styles?.button, {
        'rounded-rectangle': 'theme-button-rounded-rectangle',
        'inverse-rounded-rectangle': 'theme-button-inverse-rounded-rectangle',
        square: 'theme-button-square',
        'inverse-square': 'theme-button-inverse-square',
        pill: 'theme-button-pill',
        'inverse-pill': 'theme-button-inverse-pill',
    });

    const fontTheme = getTheme(styles?.font, {
        sans: 'theme-font-sans',
        serif: 'theme-font-serif',
        mono: 'theme-font-mono',
    });

    // Shared with custom questions, which render in their own document and so
    //  need the same numbers handed to them - see ~/lib/FormTheme
    const {
        color,
        colorLight: lightColor,
        colorLightest: lightestColor,
        fontSize,
        fontSizeSm,
    } = formThemeTokens(styles);
    const css = `
.${scope} {
    /* The theme, as custom properties, so custom CSS can restyle a form by
        overriding these rather than by outgunning our selectors */
    --breely-color: ${color};
    --breely-color-light: ${lightColor};
    --breely-color-lightest: ${lightestColor};
    --breely-font-size: ${fontSize};
    --breely-font-size-sm: ${fontSizeSm};

    font-size: var(--breely-font-size);
    .text-base, .choose-block p { font-size: var(--breely-font-size); }
    .input-label, .text-sm, .btn-light-pill-sm { font-size: var(--breely-font-size-sm); }

    &.theme-button-rounded-rectangle .form-button,
    &.theme-button-square .form-button,
    &.theme-button-pill .form-button,
    .themed-button.form-button
    { background-color: var(--breely-color); &:hover {background-color: var(--breely-color-light)} }

    &.theme-button-rounded-rectangle .form-button-light,
    &.theme-button-square .form-button-light,
    &.theme-button-pill .form-button-light,
    .themed-button.form-button-light
    { background-color: var(--breely-color-lightest); color: var(--breely-color) }

    &.theme-button-inverse-rounded-rectangle .form-button,
    &.theme-button-inverse-square .form-button,
    &.theme-button-inverse-pill .form-button,
    .themed-inverse-button.form-button
    { border-color: var(--breely-color); color: var(--breely-color); &:hover {background-color: var(--breely-color); color: #ffffff} }

    &.theme-button-inverse-rounded-rectangle .form-button-light,
    &.theme-button-inverse-square .form-button-light,
    &.theme-button-inverse-pill .form-button-light,
    .themed-inverse-button.form-button-light
    { border-color: var(--breely-color-light); color: var(--breely-color-light); &:hover {background-color: var(--breely-color-lightest); color: var(--breely-color-light)} }


    input:focus, select:focus, textarea:focus { border-color: var(--breely-color) }
    [type='checkbox'], [type='radio'] { color: var(--breely-color) }

    a, .link { color: var(--breely-color) }
    .link-no-underline { color: var(--breely-color) }

    section.choose-block:hover, section.choose-block.selected-block { border-color: var(--breely-color); outline-color: var(--breely-color); color: var(--breely-color); }
    section.choose-block:hover div:last-of-type,
    section.choose-block.selected-block div:last-of-type,
    section.choose-block.choose-block-selected div:last-of-type
        { color: var(--breely-color) }

    .availability-picker button { background-color: transparent; color: inherit }
    .time-option:hover { border-color: var(--breely-color); color: var(--breely-color) }
    button.time-option-selected { border-color: var(--breely-color); color: var(--breely-color) }
    .date-option-selected { color: var(--breely-color) !important; background-color: var(--breely-color-lightest) !important; }

    .availability-picker .react-calendar__navigation__prev-button,
    .availability-picker .react-calendar__navigation__next-button {
        color: var(--breely-color)
    }

    .availability-picker button.react-calendar__tile--active,
    .availability-picker .react-calendar__tile--active:enabled:hover,
    .availability-picker .react-calendar__tile--active:enabled:focus {
        color: var(--breely-color);
        background-color: white;
        box-shadow: 0px 0px 0px 2px var(--breely-color);
    }

    .event-confirmation-icon { color: var(--breely-color) }

    .btn-inverse, .form-button.btn-inverse { background-color: var(--breely-color-lightest); color: var(--breely-color) }
    .btn-inverse:hover, .form-button.btn-inverse:hover { background-color: var(--breely-color-lightest) }
    .btn-danger, .form-button.btn-danger { background-color: #fee2e2; color: #D25D5D }
    .btn-danger:hover, .form-button.btn-danger:hover { background-color: #fee2e2 }

    .themed-text { color: var(--breely-color) }
    .themed-outline { outline-color: var(--breely-color) }
    .themed-border { border-color: var(--breely-color) }
    .hover-themed-border:hover { border-color: var(--breely-color) }
    .themed-bg-light { background-color: var(--breely-color-lightest) }
    .themed-bg { background-color: var(--breely-color) }

    .themed-slider { --theme-color: var(--breely-color); -webkit-appearance: none; appearance: none; background: transparent; }
    .themed-slider::-webkit-slider-runnable-track {
        height: 8px;
        border-radius: 4px;
        background: linear-gradient(to right, var(--breely-color) var(--slider-progress, 50%), var(--breely-color-lightest) var(--slider-progress, 50%));
    }
    .themed-slider::-moz-range-track {
        height: 8px;
        border-radius: 4px;
        background: var(--breely-color-lightest);
        border: none;
    }
    .themed-slider::-moz-range-progress {
        height: 8px;
        border-radius: 4px;
        background: var(--breely-color);
    }
    .themed-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: white;
        cursor: pointer;
        border: 2px solid var(--breely-color);
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        margin-top: -6px;
    }
    .themed-slider::-moz-range-thumb {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: white;
        cursor: pointer;
        border: 2px solid var(--breely-color);
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }

    .thank-you-check-container { background-color: var(--breely-color-lightest); color: var(--breely-color) }
}
    `;

    // onlyColor is a swatch inside the admin styles editor rather than a form.
    //  scopeRoot is turned off where something further up already owns the
    //  root, ex. the admin designer, which themes each question separately but
    //  wants custom CSS scoped to the preview as a whole
    const isScopeRoot = !onlyColor && scopeRoot;
    const className = onlyColor
        ? `themed ${scope}`
        : `themed ${isScopeRoot ? FORM_ROOT_CLASS : ''} ${scope} ${fontTheme} ${inputTheme} ${buttonTheme}`;

    return (
        <ThemeContext.Provider
            value={{
                color: color,
                styles: styles,
            }}
        >
            <div
                className={className}
                {...(!isScopeRoot
                    ? {}
                    : formRootAttributes({
                          objectType,
                          objectId,
                          state,
                          step,
                          isEmbedded: isEmbedded(),
                      }))}
            >
                <style type="text/css">{css}</style>
                {/* Sanitized server side, and last so it can override the
                    theme above without having to outweigh its selectors */}
                {!!customCss && <style type="text/css">{customCss}</style>}
                {/* Custom questions render in their own document, which this
                    stylesheet doesn't reach, so it is handed to them too */}
                <FormCustomCssProvider css={customCss}>
                    {children}
                </FormCustomCssProvider>
            </div>
        </ThemeContext.Provider>
    );
};

export default ThemedForm;
